import { CHARACTER_SCHEMA_VERSION, type Character } from '@/schemas/character';
import { stripExpandedBundleMembers } from '@/services/bundle';
import type { CharacterDraft } from '../types';

/**
 * **FI-46bis** — Inverso parziale di
 * {@link import('@/services/characterCreation').finalizeCharacter}.
 * Trasforma un `Character` (assunto a L1 baseline, post-`rewindToLevel(c, 1)`)
 * in un {@link CharacterDraft} compatibile con `BuilderProvider` in
 * modalità edit retroattivo.
 *
 * **Pre-condizione**: `character.level === 1` e `character.levelUpHistory.length === 0`.
 * Il chiamante deve aver invocato `rewindToLevel(c, 1)` prima.
 *
 * **Why `moves` strippato dei bundle members**: `Character.moves` è
 * post-finalize (espanso, include i membri dei bundle delle anchor
 * speciali — Warlock-Tomo, Druido-DivNat, Paladino-OrdineSacro). Passare
 * tutti i moves al re-finalize duplicherebbe i membri perché `finalizeCharacter`
 * espande automaticamente quelle 3 anchor. `stripExpandedBundleMembers`
 * rimuove i membri lasciando solo l'anchor, coerente con quello che il
 * builder L1 vede normalmente nel `draft.moves` durante la creazione PG
 * ex-novo.
 *
 * **Campi non mappati** (irrilevanti per il builder):
 *  - `hp` / `armor` / `damage` / `load` — ricomputati al re-finalize
 *  - `followers` — re-derivati al re-finalize
 *  - `levelUpHistory` — vuota per L1 baseline
 *  - `level` / `xp` / `pastTen` — il builder non gestisce questi (L1 fisso)
 *  - `schemaVersion` — uguale tra Character e Draft (entrambi v2)
 *
 * **Identità preservata**: `id` e `createdAt` vengono copiati nel draft
 * per essere riapplicati al re-finalize via `FinalizeCharacterOverrides`.
 * Conseguenza: edit L1 produce un Character con stesso `id`/`createdAt` ma
 * `updatedAt` aggiornato (overwrite consistent con FI-46 PR-B).
 *
 * **Dependency flow**: vive in `features/character-builder/services/` (non
 * in `src/services/`) perché `CharacterDraft` è un tipo della feature
 * builder; consumer naturale è la stessa feature. Rispetta la regola
 * "services non importa da features" del CLAUDE.md.
 *
 * @param character PG a L1 baseline (post-`rewindToLevel(c, 1)`)
 * @returns `CharacterDraft` pre-popolato pronto per dispatch `INIT_DRAFT`
 * @throws Error se `character.level !== 1` o ha `levelUpHistory` non vuota
 */
export function characterToDraft(character: Character): CharacterDraft {
  if (character.level !== 1) {
    throw new Error(
      `characterToDraft richiede un PG a L1 baseline (chiama prima rewindToLevel). Ricevuto level=${character.level}.`,
    );
  }
  if (character.levelUpHistory.length !== 0) {
    throw new Error(
      `characterToDraft richiede levelUpHistory vuota. Ricevuto length=${character.levelUpHistory.length}.`,
    );
  }
  const draft: CharacterDraft = {
    schemaVersion: CHARACTER_SCHEMA_VERSION,
    id: character.id,
    name: character.name,
    classId: character.classId,
    archetypeId: character.archetypeId,
    race: character.race,
    alignment: character.alignment,
    stats: character.stats,
    chosenAspetti: [...character.chosenAspetti],
    moves: [...stripExpandedBundleMembers(character.moves)],
    spells: [...character.spells],
    inventory: [...character.inventory],
    bonds: [...character.bonds],
    notes: character.notes,
    createdAt: character.createdAt,
  };
  if (character.title !== undefined) draft.title = character.title;
  if (character.bondsDeferred !== undefined) draft.bondsDeferred = character.bondsDeferred;
  return draft;
}
